/**
 * Prologue cast (owner: prologue-builder). Actor ids are scene-local handles used by timelines,
 * stage placements and dialogue `face`/`emote` hooks. `actorKey` is the `@/characters` ACTOR_DEFS key
 * that provides the texture, animations, walk speed and portrait — so real art drops in there.
 *
 * Real cast sheets: the asset pipeline exports the prologue cast under the ACTOR_DEFS texture keys
 * (actor-qiong, actor-father, actor-wan-qianmei, actor-lu-baoding, actor-captain, actor-traveller,
 * actor-villager-f …) and `Actor` falls back to the dev atlas / a procedural placeholder whenever a
 * texture is absent. The generic 水手 have no `actor-sailor` sheet: `registerPrologueActorDefs()` adds
 * a prologue-only def that prefers the shared `actor-npc-sailor` sheet and falls back to the dev
 * sailor. `PROLOGUE_ART_STATES` lists the art-specific poses the real sheets carry (idle fallback on
 * dev sheets), used by the timelines through `artState()`.
 */
import type { Facing } from '@/core/types';
import type { ActorAnimState } from '@/core/types/contracts';
import { ACTOR_DEFS, type ActorDef } from '@/characters/actorDefs';

export interface PrologueActorDef {
  /** Display name (中文). */
  name: string;
  /** ACTOR_DEFS key (src/characters/actorDefs.ts, or a prologue def from `registerPrologueActorDefs`). */
  actorKey: string;
}

/** ACTOR_DEFS key of the prologue's generic sailors (registered at runtime, see below). */
export const PROLOGUE_SAILOR_KEY = 'prologue-sailor';

export const PROLOGUE_ACTORS = {
  jiang: { name: '江子潮', actorKey: 'jiang-zichao' },
  zhao: { name: '赵呈博', actorKey: 'zhao-chengbo' },
  qiong: { name: '琼', actorKey: 'qiong' },
  captain: { name: '船长', actorKey: 'captain' },
  lubaoding: { name: '卢宝丁', actorKey: 'lu-baoding' },
  wanqianmei: { name: '万千梅', actorKey: 'wan-qianmei' },
  father: { name: '父亲', actorKey: 'father' },
  mother: { name: '母亲', actorKey: 'villager-f' },
  zhaosan: { name: '赵三', actorKey: 'zhao-san' },
  returner: { name: '随船返回的人', actorKey: 'traveller' },
  'sailor-1': { name: '水手', actorKey: PROLOGUE_SAILOR_KEY },
  'sailor-2': { name: '水手', actorKey: PROLOGUE_SAILOR_KEY },
  'sailor-3': { name: '水手', actorKey: PROLOGUE_SAILOR_KEY },
  villager: { name: '村民', actorKey: 'villager-m' },
} as const satisfies Record<string, PrologueActorDef>;

export type PrologueActorId = keyof typeof PROLOGUE_ACTORS;

/**
 * Prologue-only ACTOR_DEFS entries (same pattern as the harbor / inn content modules). Idempotent;
 * never overrides a def the characters module already ships.
 */
export function prologueActorDefs(): Record<string, ActorDef> {
  const sailor = ACTOR_DEFS['sailor'];
  return {
    [PROLOGUE_SAILOR_KEY]: {
      texture: 'actor-npc-sailor',                // shared-group cast sheet (real art)
      displayName: '水手',
      speed: sailor?.speed ?? 88,
      devAtlas: sailor?.devAtlas ?? 'dev-actor-sailor',   // dev sheet when the real one is absent
      ...(sailor?.dev ? { dev: sailor.dev } : { dev: { robe: 0x1f8f8a, accent: 0xe2c48c, skin: 0xd9a878, hair: 0x2a1c14, label: 'SL' } }),
    },
  };
}

export function registerPrologueActorDefs(): void {
  for (const [key, def] of Object.entries(prologueActorDefs())) if (!ACTOR_DEFS[key]) ACTOR_DEFS[key] = def;
}

export function isPrologueActor(id: string): id is PrologueActorId {
  return Object.prototype.hasOwnProperty.call(PROLOGUE_ACTORS, id);
}

export function actorName(id: string): string {
  return (PROLOGUE_ACTORS as Record<string, PrologueActorDef>)[id]?.name ?? id;
}

export function actorKeyOf(id: string): string {
  return (PROLOGUE_ACTORS as Record<string, PrologueActorDef>)[id]?.actorKey ?? id;
}

/**
 * Art-specific poses on the real cast sheets (frame families `<state>-<dir>-<n>`; see the atlas
 * `meta.states`). The characters module registers an animation for every family a texture has and
 * `Actor.play` falls back to idle where a sheet lacks the state, so timelines may use them freely.
 */
export const PROLOGUE_ART_STATES = {
  captain: ['bow', 'pitch'],
  lubaoding: ['rite', 'tired'],
  returner: ['receive'],
  father: ['fold'],
  wanqianmei: ['chant'],
  qiong: ['cry', 'wave'],
  zhao: ['ponder'],
} as const satisfies Partial<Record<PrologueActorId, readonly string[]>>;

export type PrologueArtState = (typeof PROLOGUE_ART_STATES)[keyof typeof PROLOGUE_ART_STATES][number];

/** Types an art pose as a timeline `anim` state (the contract union names only the core states). */
export function artState(state: PrologueArtState): ActorAnimState {
  return state as unknown as ActorAnimState;
}

/**
 * Dialogue `speaker` ids. The UI dialogue box resolves ACTOR_DEFS keys to a display name and a
 * portrait; names without a def (随船返回的人) are shown as written.
 */
export const SPEAKER = {
  jiang: 'jiang-zichao',
  zhao: 'zhao-chengbo',
  qiong: 'qiong',
  captain: 'captain',
  lubaoding: 'lu-baoding',
  wanqianmei: 'wan-qianmei',
  father: 'father',
  zhaosan: 'zhao-san',
  sailor: 'sailor',
  returner: '随船返回的人',
} as const;

export interface Placement { id: PrologueActorId; x: number; y: number; facing: Facing }
